import { HttpError } from '@luminovo/api-contracts';
import { httpQuery } from '@luminovo/data-stack';
import { createFileRoute, notFound } from '@tanstack/react-router';
import type { JSX } from 'react';

import { QuoteRequestDetailsPage } from '@/areas/quote-requests/QuoteRequests/QuoteRequestDetailsPage/QuoteRequestDetailsPage';
import { sourcingViewModulePermissions } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';

// The page shows the quote request in the project's sidebar, and its links lead within the project. A quote request
// of another project or of a negotiation, or one the API doesn't know (it answers 404), renders the not-found page: a
// link never leads there, a hand-edited URL does. The loader asks for it with the query the page reads it with.
export const Route = createFileRoute(
    '/_authenticated/projects/$projectId/sourcing/_sidebar/quote-requests/$quoteRequestId',
)({
    staticData: {
        access: { permissions: ['view:sourcing'], modulePermissions: sourcingViewModulePermissions },
        chrome: 'project',
    } satisfies PageStaticData,
    loader: async ({ context: { queryClient }, params: { projectId, quoteRequestId } }) => {
        const { project } = await queryClient
            .ensureQueryData(httpQuery('GET /quote-request/:id', { pathParams: { id: quoteRequestId } }))
            .catch((error: unknown) => {
                throw error instanceof HttpError && error.status === 404 ? notFound() : error;
            });
        if (project?.type !== 'Rfq' || project.id !== projectId) {
            throw notFound();
        }
    },
    component: RouteComponent,
});

function RouteComponent(): JSX.Element {
    const { projectId, quoteRequestId } = Route.useParams();
    return <QuoteRequestDetailsPage context={{ type: 'Rfq', id: projectId }} quoteRequestId={quoteRequestId} />;
}
